import React, { useEffect, useMemo, useState } from 'react';
import {
    Heading1,
    Heading3,
    TextLead,
    ThemedButton,
} from '@awarego-ui/components';
import {
    ROLES,
    DIFFICULTIES,
    DEFAULT_ROLE,
    DEFAULT_TOPIC_ID,
    DEFAULT_DIFFICULTY,
    getTopicsForRole,
    getDefaultTopicIdForRole,
    getTopicById,
} from './constants';

export default function OwaspTrainingSetup({ onGenerate, loading, error }) {
    const [role, setRole] = useState(DEFAULT_ROLE);
    const [topicId, setTopicId] = useState(DEFAULT_TOPIC_ID);
    const [difficulty, setDifficulty] = useState(DEFAULT_DIFFICULTY);
    const [learnerHistory, setLearnerHistory] = useState(
        'completed "Intro to secure coding", quiz score 72%'
    );

    const topicsForRole = useMemo(() => getTopicsForRole(role), [role]);
    const selectedTopic = useMemo(
        () => getTopicById(role, topicId),
        [role, topicId]
    );

    useEffect(() => {
        const allowed = getTopicsForRole(role);
        const stillValid = allowed.some((t) => t.id === topicId);
        if (!stillValid && allowed.length) {
            setTopicId(allowed[0].id);
        }
    }, [role, topicId]);

    const handleRoleChange = (e) => {
        const nextRole = e.target.value;
        setRole(nextRole);
        setTopicId(getDefaultTopicIdForRole(nextRole));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const topic = getTopicById(role, topicId);
        if (!topic) return;
        onGenerate({
            role,
            topicId: topic.id,
            topic: topic.label,
            owasp: topic.owasp,
            topicFocus: topic.focus,
            difficulty,
            learnerHistory,
        });
    };

    return (
        <div className="owasp-demo-setup">
            <Heading1 mb={8}>OWASP Training (Demo)</Heading1>
            <TextLead mb={24}>
                Personalized security training powered by AI. Select your role
                and a topic relevant to that role, then generate a module with
                scenario and quiz.
            </TextLead>

            <form onSubmit={handleSubmit}>
                <div className="owasp-demo-field">
                    <label htmlFor="owasp-role">Your role</label>
                    <select
                        id="owasp-role"
                        value={role}
                        onChange={handleRoleChange}
                        disabled={loading}
                    >
                        {ROLES.map((r) => (
                            <option key={r} value={r}>
                                {r}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="owasp-demo-field">
                    <label htmlFor="owasp-topic">Training topic</label>
                    <select
                        id="owasp-topic"
                        value={topicId}
                        onChange={(e) => setTopicId(e.target.value)}
                        disabled={loading || !topicsForRole.length}
                    >
                        {topicsForRole.map((t) => (
                            <option key={t.id} value={t.id}>
                                {t.label}
                            </option>
                        ))}
                    </select>
                    {selectedTopic && selectedTopic.owasp.length ? (
                        <p className="owasp-demo-topic-hint">
                            OWASP context: {selectedTopic.owasp.join(' · ')}
                        </p>
                    ) : null}
                </div>

                <div className="owasp-demo-field">
                    <label htmlFor="owasp-difficulty">Difficulty</label>
                    <select
                        id="owasp-difficulty"
                        value={difficulty}
                        onChange={(e) => setDifficulty(e.target.value)}
                        disabled={loading}
                    >
                        {DIFFICULTIES.map((d) => (
                            <option key={d} value={d}>
                                {d}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="owasp-demo-field">
                    <label htmlFor="owasp-history">Learner history (demo)</label>
                    <input
                        id="owasp-history"
                        type="text"
                        value={learnerHistory}
                        onChange={(e) => setLearnerHistory(e.target.value)}
                        disabled={loading}
                        placeholder='e.g. completed "Intro to secure coding", quiz 72%'
                    />
                </div>

                {error ? (
                    <p className="owasp-demo-error" role="alert">
                        {error}
                    </p>
                ) : null}

                <ThemedButton
                    mode="primary"
                    type="submit"
                    text={loading ? 'Generating…' : 'Generate training module'}
                    disabled={loading || !selectedTopic}
                />
            </form>

            <Heading3 mt={32} lighter>
                Demo only — content is generated on demand and not saved to your
                catalog.
            </Heading3>
        </div>
    );
}
